// apps/api/src/qxcircle/qxcircle.types.ts
// Description: QXCircle types — Slice 1–24 follow lists.
// Owner: Masa Bayu
// Date: 2026-08-30

export type QxCircleSlice =
  | "feed"
  | "post"
  | "comment"
  | "like"
  | "media"
  | "edit"
  | "share"
  | "follow"
  | "profile"
  | "following"
  | "post-detail"
  | "bookmark"
  | "hashtag"
  | "mention"
  | "explore"
  | "block"
  | "like-notification"
  | "comment-notification"
  | "follow-notification"
  | "mute"
  | "draft"
  | "mark-all-read"
  | "reply-notification"
  | "follow-list"
  | "comment-delete"
  | "comment-edit";

export type QxCircleFeedFilter = "all" | "following";

export type QxCircleMediaKind = "image" | "video";

/** Local-only media attachment — preview for feed; not production CDN. */
export type QxCirclePostMedia = {
  kind: QxCircleMediaKind;
  contentType: string;
  fileName: string;
  byteSize: number;
  /** Opaque local ref — media://local/qxcircle/… */
  objectRef: string;
  /** data: URL for localhost feed preview (in-memory). */
  previewDataUrl: string;
};

export type QxCircleFeedPost = {
  id: string;
  authorName: string;
  authorHandle: string;
  /** Registered account that owns this post — required for new posts. */
  ownerAccountId?: string | null;
  body: string;
  likeCount: number;
  shareCount: number;
  commentCount: number;
  likedByLocal: boolean;
  savedByLocal: boolean;
  followedByLocal: boolean;
  mutedByLocal: boolean;
  /** True when local guest may edit/delete this post. */
  canManage: boolean;
  media: QxCirclePostMedia | null;
  createdAt: string;
  updatedAt: string | null;
};

export type QxCircleComment = {
  id: string;
  postId: string;
  parentId: string | null;
  authorName: string;
  authorHandle: string;
  ownerAccountId?: string | null;
  body: string;
  /** True when local guest may edit or delete this comment. */
  canManage: boolean;
  createdAt: string;
  updatedAt: string | null;
};

export type QxCircleFeedResponse = {
  items: QxCircleFeedPost[];
  filter: QxCircleFeedFilter;
  world: "qxcircle";
  slice: QxCircleSlice;
};

export type QxCirclePostDetailResponse = {
  post: QxCircleFeedPost;
  world: "qxcircle";
  slice: "post-detail";
};

export type QxCircleBookmarksResponse = {
  items: QxCircleFeedPost[];
  count: number;
  world: "qxcircle";
  slice: "bookmark";
};

export type QxCircleTagFeedResponse = {
  tag: string;
  count: number;
  items: QxCircleFeedPost[];
  world: "qxcircle";
  slice: "hashtag";
};

export type QxCircleMentionFeedResponse = {
  handle: string;
  count: number;
  items: QxCircleFeedPost[];
  world: "qxcircle";
  slice: "mention";
};

export type QxCircleTrendingTagRow = {
  tag: string;
  postCount: number;
  latestAt: string;
};

export type QxCircleTrendingTagsResponse = {
  count: number;
  items: QxCircleTrendingTagRow[];
  world: "qxcircle";
  slice: "explore";
};

export type QxCircleBlockedAuthor = {
  authorHandle: string;
  authorName: string;
};

export type QxCircleBlocksResponse = {
  count: number;
  items: QxCircleBlockedAuthor[];
  world: "qxcircle";
  slice: "block";
};

export type QxCircleBlockResult = {
  authorHandle: string;
  authorName: string;
  blockedByLocal: boolean;
  note: string;
};

export type QxCircleMutedAuthor = {
  authorHandle: string;
  authorName: string;
};

export type QxCircleMutesResponse = {
  count: number;
  items: QxCircleMutedAuthor[];
  world: "qxcircle";
  slice: "mute";
};

export type QxCircleMuteResult = {
  authorHandle: string;
  authorName: string;
  mutedByLocal: boolean;
  note: string;
};

export type QxCircleCommentsResponse = {
  postId: string;
  items: QxCircleComment[];
  world: "qxcircle";
  slice: "comment" | "like" | "media" | "edit" | "share" | "follow" | "profile";
};

export type QxCircleFollowListKind = "followers" | "following";

export type QxCircleFollowListPerson = {
  authorHandle: string;
  authorName: string;
  followedByLocal: boolean;
};

export type QxCircleFollowListResponse = {
  authorHandle: string;
  authorName: string;
  kind: QxCircleFollowListKind;
  count: number;
  items: QxCircleFollowListPerson[];
  world: "qxcircle";
  slice: "follow-list";
};

export type QxCircleAuthorProfile = {
  authorHandle: string;
  authorName: string;
  bio: string;
  followerCount: number;
  followingCount: number;
  followedByLocal: boolean;
  blockedByLocal: boolean;
  mutedByLocal: boolean;
  postCount: number;
  posts: QxCircleFeedPost[];
  world: "qxcircle";
  slice: "profile";
};

export type QxCircleNotificationKind = "follow" | "mention" | "like" | "comment" | "reply" | "system";

export type QxCircleNotification = {
  id: string;
  kind: QxCircleNotificationKind;
  title: string;
  body: string;
  href: string;
  read: boolean;
  createdAt: string;
};

export type QxCircleNotificationsResponse = {
  items: QxCircleNotification[];
  unreadCount: number;
  world: "qxcircle";
  slice: "profile";
};

export type QxCircleSearchHit = {
  authorHandle: string;
  authorName: string;
  snippet: string;
};

export type QxCircleSearchResponse = {
  query: string;
  authors: QxCircleSearchHit[];
  posts: QxCircleFeedPost[];
  world: "qxcircle";
  slice: "profile";
};

export type QxCircleReportResult = {
  postId: string;
  reason: string;
  status: "queued";
  note: string;
};

export type QxCircleModerationItem = {
  id: string;
  postId: string;
  reason: string;
  status: "queued";
  createdAt: string;
};

export type QxCircleModerationResponse = {
  items: QxCircleModerationItem[];
  world: "qxcircle";
  slice: "profile";
};

export type QxCircleFollowResult = {
  authorHandle: string;
  authorName: string;
  followedByLocal: boolean;
  note: string;
};

export type QxCircleOverview = {
  world: "qxcircle";
  title: string;
  tagline: string;
  feedCount: number;
  status: "open";
  slice: QxCircleSlice;
};

export type QxCircleShareResult = {
  postId: string;
  sharePath: string;
  shareUrl: string;
  title: string;
  text: string;
  authorName: string;
  shareCount: number;
};

export type QxCircleComposeDraft = {
  body: string;
  updatedAt: string | null;
  world: "qxcircle";
  slice: "draft";
};

export type CreateQxCirclePostMediaInput = {
  kind: QxCircleMediaKind;
  contentType: string;
  fileName: string;
  byteSize: number;
  previewDataUrl: string;
};

export type CreateQxCirclePostInput = {
  body: string;
  media?: CreateQxCirclePostMediaInput | null;
  authorName?: string;
  authorHandle?: string;
};

export type UpdateQxCirclePostInput = {
  body?: string;
  /** Pass media to replace; null to clear; omit to leave unchanged. */
  media?: CreateQxCirclePostMediaInput | null;
  clearMedia?: boolean;
};

export type CreateQxCircleCommentInput = {
  body: string;
  parentId?: string | null;
  authorName?: string;
  authorHandle?: string;
  ownerAccountId?: string;
};

export const QXCIRCLE_POST_BODY_MAX = 500;
export const QXCIRCLE_COMMENT_BODY_MAX = 200;
/** Keep JSON+base64 under Next/Nest local body limits (~20mb). */
export const QXCIRCLE_IMAGE_MAX_BYTES = 1024 * 1024;
export const QXCIRCLE_VIDEO_MAX_BYTES = 6 * 1024 * 1024;
export const QXCIRCLE_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
] as const;
export const QXCIRCLE_VIDEO_TYPES = ["video/mp4", "video/webm"] as const;
